import { createClock } from '@nedelnik/shared';

import { createSession } from '../core/auth/session.js';
import {
  META_CLOCK,
  META_DEVICE,
  META_IDENTITY,
  META_OWNER,
  clearLocal,
  readAllRecords,
  readMeta,
  writeMeta,
} from '../core/db/local.js';
import { openLocalDb } from '../core/db/schema.js';
import { createSyncEngine } from '../core/sync/engine.js';
import { createStream } from '../core/sync/stream.js';
import { createHttpTransport } from '../core/sync/transport.js';
import { useAppStore } from '../state/store.js';

import type { Identity, Session } from '../core/auth/session.js';
import type { LocalDb } from '../core/db/schema.js';
import type { SyncEngine } from '../core/sync/engine.js';
import type { StreamHandle } from '../core/sync/stream.js';
import type { HlcParts } from '@nedelnik/shared';

/**
 * Сборка клиента: база, сессия, транспорт, движок, сокет. Здесь и только здесь
 * слои знают друг о друге — сами по себе `core/db`, `core/sync` и `ui` связаны
 * лишь интерфейсами, иначе перенос слоёв 1–3 на Swift (раздел 11 архитектуры)
 * упёрся бы в React.
 */

export interface Runtime {
  db: LocalDb;
  session: Session;
  engine: SyncEngine;
  stream: StreamHandle;
  /** Принять только что вошедшего: запомнить его локально и завести синхронизацию. */
  adopt(identity: Identity): Promise<void>;
  /**
   * Забыть всё локальное: выход и удаление учётной записи.
   *
   * Стирается именно копия, а не только признак входа. Оставленные задачи —
   * это чужие задачи на общем устройстве (3.16.1), и «выйти», после которого
   * они остаются лежать, — обещание, которого приложение не выполнило.
   */
  forget(): Promise<void>;
  stop(): void;
}

let shared: Promise<Runtime> | undefined;

/**
 * Среда выполнения одна на вкладку.
 *
 * В разработке React в StrictMode монтирует эффекты дважды, и без этого
 * получались два движка и две сессии: они одновременно шли обновлять
 * refresh-токен, второй запрос заставал уже провёрнутую ротацию и получал 401.
 * Гонка настоящая, StrictMode её только проявляет.
 */
export function getRuntime(baseUrl = ''): Promise<Runtime> {
  shared ??= createRuntime(baseUrl);
  return shared;
}

export async function createRuntime(baseUrl = ''): Promise<Runtime> {
  const db = await openLocalDb();
  const session = createSession({ baseUrl });

  // Кто вошёл — известно из локальной базы, не из сети. Иначе запуск без сети
  // упирается в экран входа поверх целой копии данных: refresh-запрос падает,
  // и приложение делает вид, что не знает пользователя.
  const stored = await readMeta<Identity>(db, META_IDENTITY);
  let identity = stored;
  useAppStore.getState().setIdentity(stored);

  // Копии, заведённые до появления отдельной метки владельца, подписываются
  // здесь — при первом же запуске новой версии, то есть заведомо раньше, чем
  // сессия успеет протухнуть. Иначе на них осталась бы ровно та дыра, ради
  // которой метка и заведена.
  if (stored !== undefined && (await readMeta<string>(db, META_OWNER)) === undefined) {
    await writeMeta(db, META_OWNER, stored.id);
  }

  // Идентификатор устройства переживает перезагрузку: он входит в HLC, и новый
  // при каждом запуске сделал бы часы устройства неотличимыми от чужих.
  let deviceId = await readMeta<string>(db, META_DEVICE);
  if (deviceId === undefined) {
    deviceId = identity?.device_id ?? crypto.randomUUID();
    await writeMeta(db, META_DEVICE, deviceId);
  }

  // Часы восстанавливаются из базы: после перезагрузки вкладки они не должны
  // откатываться назад, иначе свежая правка проиграет старой при слиянии.
  const restored = await readMeta<Pick<HlcParts, 'ms' | 'counter'>>(db, META_CLOCK);

  // Часы у каждой вкладки свои, поэтому и подпись устройства в HLC — своя.
  // Две вкладки одного origin с одинаковой подписью, стартовав в одну
  // миллисекунду, выдают одинаковый HLC, а слияние считает такие правки одной
  // и той же — вторая молча пропадает.
  const clockId = `${deviceId}-${Math.random().toString(36).slice(2, 8)}`;
  const clock = createClock({
    deviceId: clockId,
    ...(restored === undefined ? {} : { restore: restored }),
  });

  const transport = createHttpTransport({
    baseUrl,
    accessToken: () => session.accessToken(),
    reauth: async () => {
      await session.refresh();
    },
  });

  const engine = createSyncEngine({
    db,
    transport,
    deviceId,
    clock,
    onStatus: (status) => useAppStore.getState().setStatus(status),
    onChange: (changes) => useAppStore.getState().applyChanges(changes),
    onRemove: (keys) => useAppStore.getState().removeRecords(keys),
    onSignedOut: () => {
      // Сервер сказал, что сессии нет: отключили устройство или вышел срок.
      // Показываем экран входа сразу, а не при следующей неудачной правке —
      // до этого вкладка честно работала с локальной копией и молча не
      // отправляла ничего.
      //
      // Копию не трогаем: она по-прежнему принадлежит этому же человеку, и
      // после входа его правки должны уехать. Чужому она не достанется —
      // подпись владельца лежит отдельно (`META_OWNER`).
      identity = undefined;
      useAppStore.getState().setIdentity(undefined);
      void writeMeta(db, META_IDENTITY, undefined);
    },
  });

  const stream = createStream({
    baseUrl,
    accessToken: () => session.accessToken(),
    onBump: (seq) => engine.bump(seq),
  });

  // Состояние часов сохраняется по ходу дела: точное значение не критично,
  // важно, что после перезапуска они не окажутся в прошлом.
  const clockTimer = setInterval(() => {
    void writeMeta(db, META_CLOCK, { ms: clock.state().ms, counter: clock.state().counter });
  }, 5_000);

  // Данные показываются из локальной копии сразу, до всякой сети.
  useAppStore.getState().hydrate(await readAllRecords(db));
  useAppStore.getState().setReady(true);

  if (identity !== undefined) {
    engine.start().catch(() => undefined);
    stream.start();
  }

  // Сессия обновляется параллельно и не задерживает открытие. Если сервер
  // ответил, что сессии больше нет, — только тогда экран входа: пока это
  // просто «не дозвонились», приложение работает оффлайн.
  void session.restore().then(
    async (fresh) => {
      if (fresh !== undefined) {
        identity = fresh;
        useAppStore.getState().setIdentity(fresh);
        await writeMeta(db, META_IDENTITY, fresh);
        await writeMeta(db, META_OWNER, fresh.id);

        if (stored === undefined) {
          engine.start().catch(() => undefined);
          stream.start();
        }
        return;
      }

      // Сюда попадаем, только если сервер ответил «сессии нет»: сетевые
      // ошибки прилетают в обработчик отказа ниже и учётную запись не трогают.
      //
      // `META_OWNER` при этом не трогается: копия осталась лежать, и она
      // по-прежнему принадлежит тому, кто её завёл. Стереть подпись здесь
      // значило бы отдать чужие задачи следующему вошедшему.
      useAppStore.getState().setIdentity(undefined);
      await writeMeta(db, META_IDENTITY, undefined);
    },
    () => undefined,
  );

  return {
    db,
    session,
    engine,
    stream,

    async adopt(value: Identity): Promise<void> {
      // Вошёл не тот, кто выходил, — локальная копия принадлежит предыдущему
      // человеку, и показывать её новому нельзя ни секунды (3.16.1). Своих
      // данных у сервера для неё нет: он отдаёт задачи по учётной записи, а
      // не по устройству, поэтому копию надо не сливать, а стирать.
      //
      // Без этого дыра была тихой: экран входа отдаёт приложение новому
      // пользователю, а в нём лежат чужие задачи — и первый же его pull
      // домешивает к ним свои.
      // Спрашиваем именно владельца копии, а не последнего вошедшего:
      // протухшая сессия стирает второе и не трогает первое.
      const previous = await readMeta<string>(db, META_OWNER);
      if (previous !== undefined && previous !== value.id) {
        engine.stop();
        stream.stop();
        await clearLocal(db);
        useAppStore.getState().reset();
        useAppStore.getState().setReady(true);
      }

      identity = value;
      useAppStore.getState().setIdentity(value);
      await writeMeta(db, META_IDENTITY, value);
      // Подпись ставится после возможной очистки: `clearLocal` сносит всю мету.
      await writeMeta(db, META_OWNER, value.id);

      engine.start().catch(() => undefined);
      stream.start();
    },

    async forget(): Promise<void> {
      engine.stop();
      stream.stop();
      await clearLocal(db);

      identity = undefined;
      useAppStore.getState().reset();
      // Готовность возвращается сразу: `reset` снимает её, а экран входа
      // показывается только поверх готового приложения — иначе человек
      // остаётся на «Открываем…» навсегда.
      useAppStore.getState().setReady(true);
    },

    stop(): void {
      clearInterval(clockTimer);
      engine.stop();
      stream.stop();
    },
  };
}
